import { useState, type ReactNode } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { unfold } from '../utils/motion';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, usePropertyAddress, EditableCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { clean, hoursRows, telHref, type OfficeHour } from '../utils/format';
import { useRawProperty } from '../utils/hooks';
import { useSiteFlags } from '../utils/site';

const Plus = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d={open ? 'M3 8h10' : 'M3 8h10M8 3v10'} />
  </svg>
);

const ANSWER =
  'max-w-[680px] text-base leading-[26px] [&_a]:font-medium [&_a]:text-[--q-primary-on-paper] [&_a]:underline-offset-[3px] [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]';

function Item({ q, children }: { q: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="border-t border-t-[--q-border] last:border-b last:border-b-[--q-border]">
      <Collapsible.Trigger className="flex w-full items-center gap-6 px-0 py-[22px] text-left text-inherit aria-expanded:pb-3 max-[767px]:gap-3 max-[767px]:py-4 max-[767px]:aria-expanded:pb-2">
        <span className="flex-auto font-heading text-[24px] leading-8 [&_p]:m-0 max-[767px]:text-[20px] max-[767px]:leading-[26px]">{q}</span>
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[--q-border] text-[--q-primary-on-paper] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-width:1.6]">
          <Plus open={open} />
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className={unfold}>
        <div className="pb-6 max-[767px]:pb-[18px]">{children}</div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/** Before you arrive (Paper 11): hours, the address, how you get in, who to call, which homes. */
export function TourQuestions() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const hours = useCollectionBinding<OfficeHour>('tourquestions.hours', { collection: 'office_hours' });
  const rows = hoursRows(hours, false);
  const address =
    clean(usePropertyAddress()) ||
    [clean(p.property_address_1), [clean(p.property_address_city), clean(p.property_address_state)].filter(Boolean).join(', '), clean(p.property_address_zip)].filter(Boolean).join(' ');
  const tel = telHref(flags.phone);
  const hasPlans = ((p.floorplans as unknown[]) || []).length > 0;

  return (
    <section className="section section--surface tqs flex items-start gap-20 max-[1199px]:flex-col max-[1199px]:gap-10 max-[767px]:gap-7">
      <div className="flex flex-[0_0_360px] flex-col gap-4 [&_.h2_p]:m-0 max-[1199px]:basis-auto" data-reveal>
        <RichTextContent path="tourquestions.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="tourquestions.title" as="h2" className="h2" />
        <RichTextContent
          path="tourquestions.lead"
          as="p"
          className="m-0 text-base leading-6 text-muted-foreground [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]"
        />
      </div>
      <div className="min-w-0 max-w-[808px] flex-auto max-[1199px]:w-full max-[1199px]:max-w-none">
        <Item q={<RichTextContent path="tourquestions.when_q" as="span" />}>
          <EditableCollectionBinding bindingPath="tourquestions.hours" label="Office Hours">
            <div className={`${ANSWER} flex flex-col gap-2`}>
              {rows.length > 0 && (
                <div className="hours">
                  {rows.map(([d, t]) => (
                    <div className="hours__row" key={d}>
                      <span>{d}</span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              )}
              <RichTextContent path="tourquestions.when_a" as="div" />
            </div>
          </EditableCollectionBinding>
        </Item>
        {address && (
          <Item q={<RichTextContent path="tourquestions.where_q" as="span" />}>
            <RichTextContent path="tourquestions.where_a" as="div" className={ANSWER} />
          </Item>
        )}
        <Item q={<RichTextContent path="tourquestions.how_q" as="span" />}>
          <RichTextContent path="tourquestions.how_a" as="div" className={ANSWER} />
        </Item>
        {tel && (
          <Item q={<RichTextContent path="tourquestions.who_q" as="span" />}>
            <div className={ANSWER}>
              <RichTextContent path="tourquestions.who_a" as="div" />
              <a className="dniphone dniphonehref" href={tel}>{flags.phone}</a>
            </div>
          </Item>
        )}
        {hasPlans && (
          <Item q={<RichTextContent path="tourquestions.homes_q" as="span" />}>
            <RichTextContent path="tourquestions.homes_a" as="div" className={ANSWER} />
          </Item>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourQuestions',
  displayName: 'Before You Arrive',
  tokens: {
    'tourquestions.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Before you arrive</p>' },
    'tourquestions.title': { label: 'Title', type: 'richtext', defaultValue: '<p>A few things worth knowing.</p>' },
    'tourquestions.lead': { label: 'Lead', type: 'richtext', defaultValue: '<p>Hours, the address, and how you get in.</p>' },
    'tourquestions.when_q': { label: 'When · Question', type: 'richtext', defaultValue: '<p>When can I visit?</p>' },
    'tourquestions.when_a': {
      label: 'When · Answer',
      description: 'Shown under the office hours.',
      type: 'richtext',
      defaultValue: '<p>The scheduler shows the times you can book.</p>',
    },
    'tourquestions.where_q': { label: 'Where · Question', type: 'richtext', defaultValue: '<p>Where do I go?</p>' },
    'tourquestions.where_a': {
      label: 'Where · Answer',
      description: 'The full address, linked to the Google place page.',
      type: 'richtext',
      defaultValue:
        '<p><a data-field-href="property_google_place_url" target="_blank" rel="noopener"><span data-type="field-token" data-field="property_full_address"></span></a></p>',
    },
    'tourquestions.how_q': { label: 'Access · Question', type: 'richtext', defaultValue: '<p>How do I get in?</p>' },
    'tourquestions.how_a': {
      label: 'Access · Answer',
      type: 'richtext',
      defaultValue: '<p>Choose a time in the scheduler. Your access details are sent before you arrive.</p>',
    },
    'tourquestions.who_q': { label: 'Call · Question', type: 'richtext', defaultValue: '<p>Who can I call?</p>' },
    'tourquestions.who_a': {
      label: 'Call · Answer',
      description: 'Shown above the leasing phone number.',
      type: 'richtext',
      defaultValue: '<p>Call the leasing team at</p>',
    },
    'tourquestions.homes_q': { label: 'Homes · Question', type: 'richtext', defaultValue: '<p>Which homes can I see?</p>' },
    'tourquestions.homes_a': {
      label: 'Homes · Answer',
      type: 'richtext',
      defaultValue: '<p>Any available home with <span data-type="field-token" data-field="property_bedrooms_range"></span> bedrooms, across every floor plan at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
  },
  collectionBindings: {
    'tourquestions.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(TourQuestions as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourQuestions;
